import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, TextField, Typography } from '@mui/material'
import {
  canCreateCrosswalk,
  currentSheetRawDataAtom,
  parsedCurrentSheetAtom,
} from './atoms'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { RawDataTable } from './RawDataTable'
import { ParsedData } from './ParsedData'
import { SpreadsheetSheetSelector } from 'components/tree/googleApis/spreadsheets/SpreadsheetSheetSelector'
import { Frameworks } from './Frameworks'
import {
  useCurrentCrosswalk,
  useCurrentCrosswalks,
  useNewCrosswalkData,
} from './hooks'
import { CrosswalkMappingSetType } from 'domains/scope-and-sequence/types'
import { ItemSelect } from 'components/utility/ItemSelect'
import { fetchUser } from 'shared/api/user'
import { ConfirmWithDialogButton } from 'shared/ui'
import { useCreateNewCrosswalk } from 'domains/scope-and-sequence/hooks'

export function CrosswalksParser() {
  const setter = useSetAtom(currentSheetRawDataAtom)
  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Add New
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Spreadsheet must include: Column A: Grade | Column B: State
            Standards | Column C: Map to CCSS | Column D: Rating | Column E:
            Notes
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Import Crosswalk Spreadsheet
          </Typography>
          <Box padding="5px">
            <SpreadsheetSheetSelector onSheetDataChange={setter} />
          </Box>
          <Frameworks />
          <FileContents />
          <Crosswalks />
        </CardContent>
      </Card>
    </Box>
  )
}

function FileContents() {
  const parsed = useAtomValue(parsedCurrentSheetAtom)
  if (!parsed) return null
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Import Preview
      </Typography>
      <RawDataTable />
      <ParsedData />
    </Box>
  )
}

function Crosswalks() {
  const canCreate = useAtomValue(canCreateCrosswalk)
  const existing = useCurrentCrosswalks()
  const doCreate = useCreateNewCrosswalk()

  if (!existing) return null
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Crosswalks
      </Typography>
      <ExistingCrosswalks crosswalks={existing} />
      <Box sx={{ mt: 1 }}>
        <ConfirmWithDialogButton
          dialogTitle={'Create New Crosswalk'}
          dialogContent={<NewCrosswalkInputs />}
          onClick={doCreate}
          canConfirm={canCreate}
        >
          Create New Crosswalk
        </ConfirmWithDialogButton>
      </Box>
    </Box>
  )
}

function NewCrosswalkInputs() {
  const { sourceFramework, targetFramework, userName, setData } =
    useNewCrosswalkData()
  if (!sourceFramework || !targetFramework) return null
  return (
    <Box>
      <Box padding="5px">
        <TextField
          fullWidth
          label="Name"
          onChange={(e) =>
            setData((prev) => ({ ...prev, name: e.target.value }))
          }
        />
      </Box>
      <Box padding="5px">
        <TextField
          fullWidth
          label="Description"
          onChange={(e) =>
            setData((prev) => ({ ...prev, description: e.target.value }))
          }
        />
      </Box>
      <Box padding="5px">{`Source Framework: ${sourceFramework.name}`}</Box>
      <Box padding="5px">{`Target Framework: ${targetFramework.name}`}</Box>
      <Box padding="5px">{`Created By: ${userName}`}</Box>
    </Box>
  )
}

function ExistingCrosswalks({
  crosswalks,
}: {
  crosswalks: CrosswalkMappingSetType[]
}) {
  const { currentCrosswalk, setCurrentCrosswalk } = useCurrentCrosswalk()
  const [crosswalkUser, setCrosswalkUser] = useState<{
    email: string
    name: string
  }>()

  useEffect(() => {
    if (!currentCrosswalk?.created_by) return
    fetchUser(Number(currentCrosswalk.created_by)).then(setCrosswalkUser)
  }, [currentCrosswalk?.created_by])

  if (!crosswalks.length) {
    return <Box>No Crosswalks For These Frameworks Found</Box>
  }

  return (
    <Box>
      <ItemSelect<CrosswalkMappingSetType, number>
        currentVal={currentCrosswalk?.id}
        fullWidth={true}
        label="Select Crosswalk"
        items={crosswalks}
        onValChange={(val) => setCurrentCrosswalk(val)}
        itemDisplay={({ name }) => name}
        itemValue={({ id }) => id}
      />
      {currentCrosswalk && (
        <Box padding="5px">
          <Box>{`Created By: ${crosswalkUser?.name ?? currentCrosswalk.created_by}`}</Box>
        </Box>
      )}
    </Box>
  )
}
